import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, MessageCircle, Clock, CheckCircle, Truck, XCircle,
  Send, FileText, Loader2, ChevronRight, User,
  Check, X, StickyNote, ArrowRight, ChevronDown, Bell, Edit3
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { EmptyStatePresets } from "../components/EmptyState";

// --- Config ---
const STATUS_CONFIG = {
  new: { label: "Nouvelle", color: "bg-blue-500", hex: "#3B82F6", icon: Package },
  discussing: { label: "En discussion", color: "bg-yellow-500", hex: "#EAB308", icon: MessageCircle },
  confirmed: { label: "Confirmée", color: "bg-green-500", hex: "#22C55E", icon: CheckCircle },
  en_cours: { label: "En cours", color: "bg-orange-500", hex: "#F97316", icon: Clock },
  shipped: { label: "Expédiée", color: "bg-purple-500", hex: "#8B5CF6", icon: Truck },
  completed: { label: "Terminée", color: "bg-gray-500", hex: "#6B7280", icon: Check },
  cancelled: { label: "Annulée", color: "bg-red-500", hex: "#EF4444", icon: XCircle }
};

const PIPELINE_STEPS = ["new", "confirmed", "en_cours", "shipped", "completed"];
const PIPELINE_LABELS = { new: "Nouvelle", confirmed: "Confirmée", en_cours: "En cours", shipped: "Expédiée", completed: "Terminée" };

function getNotificationTemplate(status, customerName, orderRef) {
  const templates = {
    confirmed: `Bonjour ${customerName}, votre commande ${orderRef} a été confirmée par la boutique. Nous préparons votre colis.`,
    en_cours: `Bonjour ${customerName}, votre commande ${orderRef} est en cours de préparation. Vous serez notifié(e) dès l'expédition.`,
    shipped: `Bonjour ${customerName}, votre commande ${orderRef} a été expédiée ! Elle sera bientôt chez vous.`,
    completed: `Bonjour ${customerName}, votre commande ${orderRef} a été marquée comme livrée. Merci pour votre confiance !`,
    cancelled: `Bonjour ${customerName}, votre commande ${orderRef} a été annulée. Contactez-nous pour plus d'informations.`,
  };
  return templates[status] || "";
}

// --- Theme helper ---
function panelTheme(isDark) {
  return isDark ? {
    bg: "#1C1C1E",
    border: "1px solid rgba(255,255,255,0.08)",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.45)",
    divider: "rgba(255,255,255,0.07)",
    inputBg: "rgba(255,255,255,0.05)",
    inputBorder: "rgba(255,255,255,0.10)",
    inputText: "rgba(255,255,255,0.8)",
    closeBg: "rgba(255,255,255,0.08)",
    cardBg: "rgba(255,255,255,0.04)",
    mutedBg: "rgba(255,255,255,0.06)",
  } : {
    bg: "#FFFFFF",
    border: "1px solid #E5E7EB",
    text: "#111111",
    muted: "#6B7280",
    divider: "#F3F4F6",
    inputBg: "#F9FAFB",
    inputBorder: "#E5E7EB",
    inputText: "#111111",
    closeBg: "#F3F4F6",
    cardBg: "#F9FAFB",
    mutedBg: "#F3F4F6",
  };
}

export default function Orders() {
  const { getAuthHeaders, user } = useAuth();
  const { isDark } = useOutletContext() || {};
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Notification message states
  const [notifExpanded, setNotifExpanded] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);

  const boutiqueData = JSON.parse(localStorage.getItem("boutique") || "{}");
  const devise = boutiqueData.currencySymbol || boutiqueData.currency_symbol || "€";
  const formatPrice = (price) => {
    if (price == null || isNaN(price)) return `0 ${devise}`;
    return `${Number(price).toLocaleString("fr-FR")} ${devise}`;
  };

  const fetchOrders = async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/orders${statusFilter !== "all" ? `?status=${statusFilter}` : ""}`, getAuthHeaders()),
        axios.get(`${API}/orders/stats`, getAuthHeaders())
      ]);
      setOrders(ordersRes.data);
      setStats(statsRes.data);
    } catch { toast.error("Erreur lors du chargement des commandes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, [statusFilter]);

  const openPanel = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}`, getAuthHeaders());
      setSelectedOrder(res.data);
      setNoteText(res.data.internal_note || "");
      setPanelOpen(true);
      setNotifExpanded(false);
      setPendingStatus(null);
      setNotifMessage("");
      fetchOrders();
    } catch { toast.error("Erreur lors du chargement"); }
  };

  const closePanel = () => { setPanelOpen(false); setTimeout(() => { setSelectedOrder(null); setPendingStatus(null); }, 300); };

  // Phase 1: prepare the status update & show message preview
  const prepareStatusUpdate = (newStatus) => {
    if (!selectedOrder) return;
    const template = getNotificationTemplate(newStatus, selectedOrder.customer_name || "", selectedOrder.order_number || "");
    setNotifMessage(template);
    setPendingStatus(newStatus);
    setNotifExpanded(true);
  };

  // Phase 2: confirm & send
  const confirmStatusUpdate = async () => {
    if (!selectedOrder || !pendingStatus) return;
    setActionLoading(true);
    try {
      const res = await axios.put(`${API}/orders/${selectedOrder.id}/status`, {
        status: pendingStatus,
        notification_message: notifMessage || null
      }, getAuthHeaders());
      const detail = await axios.get(`${API}/orders/${selectedOrder.id}`, getAuthHeaders());
      setSelectedOrder(detail.data);
      fetchOrders();
      const cName = res.data.customer_name || selectedOrder.customer_name || "";
      if (res.data.notification_sent) {
        toast.success(`Statut mis à jour · Message envoyé à ${cName}`, { duration: 3000, style: { background: "#DCFCE7", color: "#166534", border: "1px solid #BBF7D0" } });
      } else {
        toast.success("Statut mis à jour");
      }
      setPendingStatus(null);
      setNotifExpanded(false);
      if (pendingStatus === "completed") setTimeout(closePanel, 1500);
    } catch { toast.error("Erreur de mise à jour"); }
    finally { setActionLoading(false); }
  };

  const cancelPendingStatus = () => { setPendingStatus(null); setNotifExpanded(false); };

  const saveNote = async () => {
    if (!selectedOrder) return;
    setSavingNote(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/note`, { note: noteText }, getAuthHeaders());
      toast.success("Note enregistrée");
    } catch { toast.error("Erreur"); }
    finally { setSavingNote(false); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const t = panelTheme(isDark);

  // --- Action button builder ---
  const ActionBtn = ({ status, label, icon: Icon, color }) => {
    const isActive = pendingStatus === status;
    return (
      <button data-testid={`action-${status}`} onClick={() => isActive ? cancelPendingStatus() : prepareStatusUpdate(status)} disabled={actionLoading}
        style={{
          width: "100%", padding: 12, borderRadius: 10,
          background: isActive ? `${color}22` : color,
          color: isActive ? color : "#fff",
          border: isActive ? `2px solid ${color}` : "none",
          fontWeight: 700, fontSize: 14, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          opacity: actionLoading ? 0.6 : 1, transition: "all 200ms",
        }}>
        {actionLoading && isActive ? <Loader2 size={16} className="animate-spin" /> : <><Icon size={16} /> {label}</>}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Commandes</h1>
          <p className="text-muted-foreground text-sm mt-1">Gérez les commandes et échangez avec vos clientes</p>
        </div>
        {stats?.unread_messages > 0 && (
          <Badge className="bg-red-500 text-white animate-pulse"><MessageCircle className="w-4 h-4 mr-1" />{stats.unread_messages} nouveau(x) message(s)</Badge>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const count = stats[key] || 0;
            const Icon = config.icon;
            return (
              <Card key={key} className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === key ? "ring-2 ring-primary" : ""}`}
                onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}>
                <CardContent className="p-4 text-center">
                  <div className={`w-10 h-10 rounded-full ${config.color} mx-auto mb-2 flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Orders List */}
      {orders.length === 0 ? (
        statusFilter !== "all" ? (
          <EmptyStatePresets.NoSearchResults searchTerm={`statut: ${STATUS_CONFIG[statusFilter]?.label || statusFilter}`} />
        ) : (
          <EmptyStatePresets.NoOrders onAction={() => window.open(`/vitrine/${user?.id}`, "_blank")} />
        )
      ) : (
        <div className="space-y-2">
          {orders.map(order => {
            const sc = STATUS_CONFIG[order.status];
            const Icon = sc?.icon || Package;
            const total = order.final_price || order.proposed_price || order.total || order.subtotal || 0;
            return (
              <div key={order.id} data-testid={`order-${order.id}`}
                onClick={() => openPanel(order)}
                className="group"
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                  background: "var(--card)", border: "1px solid var(--border)",
                  borderLeft: "3px solid transparent",
                  transition: "all 150ms",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,69,0,0.03)"; e.currentTarget.style.borderLeftColor = "#FF4500"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--card)"; e.currentTarget.style.borderLeftColor = "transparent"; }}
              >
                <div className={`w-11 h-11 rounded-full ${sc?.color || "bg-gray-500"} flex items-center justify-center shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontWeight: 700, fontSize: 13 }}>{order.order_number}</span>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 12,
                      background: `${sc?.hex || "#6B7280"}22`,
                      color: sc?.hex || "#6B7280",
                      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                    }}>{sc?.label}</span>
                    {order.source === "proposition" && (
                      <span style={{ padding: "2px 8px", borderRadius: 12, background: "rgba(139,92,246,0.1)", color: "#8B5CF6", fontSize: 10, fontWeight: 600 }}>Via chat</span>
                    )}
                    {order.unread_messages > 0 && (
                      <span style={{ padding: "2px 6px", borderRadius: 10, background: "#EF4444", color: "#fff", fontSize: 10, fontWeight: 700 }}>{order.unread_messages} msg</span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--muted-foreground)" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 3 }}><User size={11} />{order.customer_name}</span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>{order.items?.length || 0} article(s)</span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#FF4500" }}>{formatPrice(total)}</div>
                </div>
                <ChevronRight size={18} style={{ color: "var(--muted-foreground)", opacity: 0.5, flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Slide-over Panel ── */}
      <AnimatePresence>
        {panelOpen && (
          <>
            {/* Overlay — always same in both modes */}
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={closePanel}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 9998 }}
            />
            {/* Panel — forced theme colors */}
            <motion.div
              data-testid="order-detail-panel"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="slide-panel"
              style={{
                position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
                maxWidth: "100vw",
                background: t.bg,
                borderLeft: t.border,
                color: t.text,
                zIndex: 9999, display: "flex", flexDirection: "column",
                boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
              }}
            >
              {selectedOrder && (
                <div style={{ flex: 1, overflow: "auto", padding: 24 }}>
                  {/* 1. HEADER */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: t.text }}>{selectedOrder.order_number}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                        <span style={{
                          display: "inline-block", padding: "3px 10px", borderRadius: 14,
                          background: `${STATUS_CONFIG[selectedOrder.status]?.hex || "#6B7280"}22`,
                          color: STATUS_CONFIG[selectedOrder.status]?.hex || "#6B7280",
                          fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                        }}>
                          {STATUS_CONFIG[selectedOrder.status]?.label}
                        </span>
                        {selectedOrder.source === "proposition" && (
                          <span style={{ padding: "3px 10px", borderRadius: 14, background: "rgba(139,92,246,0.1)", color: "#8B5CF6", fontSize: 11, fontWeight: 600 }}>Via chat</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: t.muted, marginTop: 6 }}>
                        {formatDate(selectedOrder.created_at)}
                      </div>
                    </div>
                    <button data-testid="close-panel-btn" onClick={closePanel}
                      style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: t.closeBg, border: "none", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: t.muted,
                      }}>
                      <X size={16} />
                    </button>
                  </div>

                  {/* 2. CUSTOMER */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: t.muted, letterSpacing: 0.5, marginBottom: 10 }}>Cliente</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                        background: "linear-gradient(135deg, #FF450044, #FF450022)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 800, color: "#FF4500",
                      }}>
                        {(selectedOrder.customer_name || "?").substring(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>{selectedOrder.customer_name}</div>
                        {selectedOrder.customer_email && (
                          <div style={{ fontSize: 12, color: t.muted }}>{selectedOrder.customer_email}</div>
                        )}
                        {selectedOrder.customer_phone && (
                          <div style={{ fontSize: 12, color: t.muted }}>{selectedOrder.customer_phone}</div>
                        )}
                      </div>
                    </div>
                    {selectedOrder.conversation_id && (
                      <a href="/admin/conversations" data-testid="go-to-messages"
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: 12, color: "#FF4500", textDecoration: "none", fontWeight: 600 }}>
                        <MessageCircle size={12} /> Envoyer un message <ArrowRight size={10} />
                      </a>
                    )}
                  </div>

                  <hr style={{ border: "none", borderTop: `1px solid ${t.divider}`, margin: "0 0 20px" }} />

                  {/* 4. ARTICLES */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: t.muted, letterSpacing: 0.5, marginBottom: 10 }}>Articles</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {selectedOrder.items?.map((item, idx) => (
                        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          {item.image_url ? (
                            <img src={item.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 8, background: t.mutedBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <Package size={16} style={{ color: t.muted }} />
                            </div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: t.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.product_name}
                            </div>
                            <div style={{ fontSize: 11, color: t.muted }}>
                              {[item.size, item.color].filter(Boolean).join(" · ")}
                              {item.quantity > 1 && ` · x${item.quantity}`}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: t.text, flexShrink: 0 }}>
                            {formatPrice((item.unit_price || item.price || 0) * (item.quantity || 1))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <hr style={{ border: "none", borderTop: `1px solid ${t.divider}`, margin: "0 0 20px" }} />

                  {/* 6. TOTAUX */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, color: t.muted }}>
                      <span>Sous-total</span>
                      <span>{formatPrice(selectedOrder.subtotal || selectedOrder.total || 0)}</span>
                    </div>
                    {(selectedOrder.remise > 0 || selectedOrder.discount > 0) && (
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6, color: "#22C55E" }}>
                        <span>Remise</span>
                        <span>-{formatPrice(selectedOrder.remise || selectedOrder.discount || 0)}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, marginTop: 8, color: t.text }}>
                      <span>TOTAL</span>
                      <span style={{ color: "#FF4500" }}>
                        {formatPrice(selectedOrder.final_price || selectedOrder.total || selectedOrder.subtotal || 0)}
                      </span>
                    </div>
                    {selectedOrder.mode_paiement && selectedOrder.mode_paiement !== "en_attente" && (
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 6 }}>
                        Paiement : {selectedOrder.mode_paiement.replace(/_/g, " ")}
                      </div>
                    )}
                  </div>

                  <hr style={{ border: "none", borderTop: `1px solid ${t.divider}`, margin: "0 0 20px" }} />

                  {/* 7. STATUS & ACTIONS */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: t.muted, letterSpacing: 0.5, marginBottom: 14 }}>Statut de la commande</div>

                    {/* Pipeline */}
                    {selectedOrder.status !== "cancelled" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20, overflow: "auto" }}>
                        {PIPELINE_STEPS.map((step, i) => {
                          const currentIdx = PIPELINE_STEPS.indexOf(
                            selectedOrder.status === "discussing" ? "new" : selectedOrder.status
                          );
                          const isPast = i < currentIdx;
                          const isCurrent = i === currentIdx;
                          const isFuture = i > currentIdx;
                          const dotColor = isPast ? "#10B981" : isCurrent ? "#FF4500" : (isDark ? "#3A3A3C" : "#E5E7EB");
                          const lineColor = isPast ? "#10B981" : (isDark ? "#3A3A3C" : "#E5E7EB");
                          return (
                            <div key={step} style={{ display: "flex", alignItems: "center", flex: i < PIPELINE_STEPS.length - 1 ? 1 : "none" }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 50 }}>
                                <div style={{
                                  width: isCurrent ? 14 : 10, height: isCurrent ? 14 : 10, borderRadius: "50%",
                                  background: dotColor, transition: "all 200ms",
                                  boxShadow: isCurrent ? `0 0 0 4px ${dotColor}33` : "none",
                                }} />
                                <span style={{ fontSize: 9, fontWeight: isCurrent ? 700 : 500, color: isFuture ? (isDark ? "rgba(255,255,255,0.25)" : "#D1D5DB") : t.text, textAlign: "center", whiteSpace: "nowrap" }}>
                                  {PIPELINE_LABELS[step]}
                                </span>
                              </div>
                              {i < PIPELINE_STEPS.length - 1 && (
                                <div style={{ flex: 1, height: 2, background: lineColor, marginBottom: 18, minWidth: 12 }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Action buttons */}
                    {selectedOrder.status === "new" && !pendingStatus && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <ActionBtn status="confirmed" label="Confirmer la commande" icon={CheckCircle} color="#FF4500" />
                        <button data-testid="action-cancel-order" onClick={() => prepareStatusUpdate("cancelled")} disabled={actionLoading}
                          style={{ width: "100%", padding: 10, borderRadius: 10, background: "transparent", color: "#EF4444", border: "1px solid rgba(239,68,68,0.3)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                          Annuler
                        </button>
                      </div>
                    )}
                    {selectedOrder.status === "confirmed" && !pendingStatus && (
                      <ActionBtn status="en_cours" label="Marquer en cours de préparation" icon={Clock} color="#3B82F6" />
                    )}
                    {selectedOrder.status === "en_cours" && !pendingStatus && (
                      <ActionBtn status="shipped" label="Marquer comme expédiée" icon={Truck} color="#8B5CF6" />
                    )}
                    {selectedOrder.status === "shipped" && !pendingStatus && (
                      <ActionBtn status="completed" label="Confirmer la livraison" icon={CheckCircle} color="#10B981" />
                    )}
                    {selectedOrder.status === "completed" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 10, background: "rgba(16,185,129,0.1)", color: "#10B981" }}>
                        <CheckCircle size={18} /> <span style={{ fontWeight: 600, fontSize: 14 }}>Commande terminée</span>
                      </div>
                    )}
                    {selectedOrder.status === "cancelled" && !pendingStatus && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", borderRadius: 10, background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                        <XCircle size={18} /> <span style={{ fontWeight: 600, fontSize: 14 }}>Commande annulée</span>
                      </div>
                    )}

                    {/* STEP D: Notification message preview */}
                    <AnimatePresence>
                      {pendingStatus && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          style={{ overflow: "hidden", marginTop: 12 }}
                        >
                          <div style={{
                            background: t.cardBg, border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#E5E7EB"}`,
                            borderRadius: 12, padding: 16,
                          }}>
                            <button data-testid="toggle-notif-preview" onClick={() => setNotifExpanded(!notifExpanded)}
                              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0, color: t.text }}>
                              <Bell size={14} style={{ color: "#FF4500" }} />
                              <span style={{ fontWeight: 600, fontSize: 13, flex: 1, textAlign: "left" }}>Message envoyé automatiquement</span>
                              <ChevronDown size={14} style={{ color: t.muted, transform: notifExpanded ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
                            </button>
                            {notifExpanded && (
                              <div style={{ marginTop: 12 }}>
                                <div style={{ position: "relative" }}>
                                  <textarea
                                    data-testid="notif-message-textarea"
                                    value={notifMessage}
                                    onChange={e => setNotifMessage(e.target.value)}
                                    rows={4}
                                    style={{
                                      width: "100%", fontSize: 12, lineHeight: 1.5,
                                      border: `1px solid ${t.inputBorder}`,
                                      borderRadius: 8, padding: "10px 12px",
                                      background: t.inputBg, color: t.inputText,
                                      resize: "none", outline: "none", fontFamily: "inherit",
                                    }}
                                  />
                                  <Edit3 size={12} style={{ position: "absolute", top: 10, right: 10, color: t.muted, opacity: 0.5 }} />
                                </div>
                                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                  <button data-testid="confirm-status-btn" onClick={confirmStatusUpdate} disabled={actionLoading}
                                    style={{
                                      flex: 1, padding: "10px 16px", borderRadius: 8,
                                      background: "#FF4500", color: "#fff", border: "none",
                                      fontWeight: 700, fontSize: 13, cursor: "pointer",
                                      display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                                      opacity: actionLoading ? 0.6 : 1,
                                    }}>
                                    {actionLoading ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Envoyer avec ce message</>}
                                  </button>
                                  <button data-testid="cancel-status-btn" onClick={cancelPendingStatus}
                                    style={{
                                      padding: "10px 14px", borderRadius: 8,
                                      background: t.mutedBg, color: t.muted, border: "none",
                                      fontWeight: 600, fontSize: 13, cursor: "pointer",
                                    }}>
                                    Annuler
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Invoice link */}
                    {selectedOrder.invoice_id && (
                      <button onClick={() => window.open(`/invoice/${selectedOrder.invoice_id}`, "_blank")}
                        style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: "#FF4500", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
                        <FileText size={12} /> Voir la facture <ArrowRight size={10} />
                      </button>
                    )}
                  </div>

                  <hr style={{ border: "none", borderTop: `1px solid ${t.divider}`, margin: "0 0 20px" }} />

                  {/* 8. NOTES */}
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: t.muted, letterSpacing: 0.5, marginBottom: 10, display: "flex", alignItems: "center", gap: 4 }}>
                      <StickyNote size={10} /> Note interne
                    </div>
                    <textarea
                      data-testid="order-note"
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      placeholder="Ajouter une note interne..."
                      rows={3}
                      style={{
                        width: "100%", fontSize: 12,
                        border: `1px solid ${t.inputBorder}`,
                        borderRadius: 8, padding: 10,
                        background: t.inputBg, color: t.inputText,
                        resize: "none", outline: "none", fontFamily: "inherit",
                      }}
                    />
                    <button onClick={saveNote} disabled={savingNote}
                      style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: "#FF4500", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                      {savingNote ? "Enregistrement..." : "Enregistrer la note"}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
